'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { Heading } from 'design-system/client';
import { useTranslations, useLocale } from 'next-intl';
import analytics from 'product-analytics';
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import styles from './StickyBar.module.css';
import ButtonLink from '@/components/ButtonLink';
import { Routes } from '@/helpers/utils';
type StickyBarProps = {
  donationFormUrl: string;
  children: ReactNode;
  spyRef?: Readonly<RefObject<HTMLDivElement | null>>;
  mobileBar?: boolean;
  fundraisingEventId: string;
  slug: string;
  title: string;
};
type StickBarContentProps = Pick<StickyBarProps, 'children' | 'donationFormUrl' | 'mobileBar' | 'fundraisingEventId' | 'slug' | 'title'> & {
  stickyRef?: Readonly<RefObject<HTMLDivElement | null>>;
  inert?: boolean;
};
function StickyBarContent({
  children,
  donationFormUrl,
  stickyRef,
  mobileBar,
  inert,
  fundraisingEventId,
  slug,
  title
}: StickBarContentProps) {
  const t = useTranslations('nextjs.core');
  const locale = useLocale();
  const trackDonationButtonClicked = useCallback(() => {
    analytics.donationButtonClicked({
      id: fundraisingEventId,
      receiver_type: 'FundraisingEvent',
      button_location: 'FunE Sticky'
    });
  }, [fundraisingEventId]);
  const trackShareButtonClicked = useCallback(() => {
    analytics.shareButtonClicked({
      id: fundraisingEventId,
      page_type: 'fundraising_event',
      title,
      button_location: 'fun_e_sticky'
    });
  }, [fundraisingEventId, title]);
  return <div className={classNames(styles.container, {
    'bottom-bar': mobileBar,
    [styles.topBar!]: !mobileBar
  })} ref={stickyRef} inert={inert && '' as unknown as boolean}>
      <div className={styles.innerContainer}>
        <Heading as="h2" level="heading-xs" className={styles.heading}>
          {children}
        </Heading>
        <div className={styles.buttonContainer}>
          <ButtonLink kind="secondary" className={styles.button} href={Routes.fundraisingEventsSharingOptions({
          slug,
          locale,
          utm_campaign: 'user_share',
          utm_medium: 'fepp_sticky'
        })} onClick={trackShareButtonClicked}>
            {t('share_button')}
          </ButtonLink>
          <ButtonLink id="sticky-bar-donate-button" href={donationFormUrl} className={styles.button} onClick={trackDonationButtonClicked}>
            {t('donate_button')}
          </ButtonLink>
        </div>
      </div>
    </div>;
}
const StickyBarState = {
  On: 'on',
  Off: 'off'
} as const;
type StickyBarState = (typeof StickyBarState)[keyof typeof StickyBarState];
function StickyBar(t0) {
  const $ = _c(12);
  const {
    spyRef,
    donationFormUrl,
    children,
    mobileBar,
    fundraisingEventId,
    slug,
    title
  } = t0;
  const stickyRef = useRef(null);
  const [state, setState] = useState(StickyBarState.Off);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      stickyRef.current?.classList.remove(styles.show);
      stickyRef.current?.classList.remove(styles.hide);
      setState(StickyBarState.Off);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const removeBar = t1;
  let t2;
  let t3;
  if ($[1] !== spyRef || $[2] !== state) {
    t2 = () => {
      if (!spyRef?.current) {
        return;
      }
      const controller = new AbortController();
      const spyElement = spyRef.current;
      const stickyObserver = new IntersectionObserver(entries => {
        const isIntersecting = !entries[0]?.isIntersecting;
        const boundingClientTop = entries[0]?.boundingClientRect.top;
        const isBelowTheFold = !!boundingClientTop && boundingClientTop > 0;
        if (!isBelowTheFold && isIntersecting && state === StickyBarState.Off) {
          stickyRef.current?.classList.add(styles.show);
          setState(StickyBarState.On);
        }
        if (!isIntersecting && state === StickyBarState.On) {
          stickyRef.current?.classList.add(styles.hide);
          stickyRef.current?.addEventListener("animationend", removeBar, {
            once: true,
            signal: controller.signal
          });
        }
      }, {
        root: null,
        rootMargin: "0px",
        threshold: 0
      });
      stickyObserver.observe(spyElement);
      return () => {
        controller.abort();
        stickyObserver.unobserve(spyElement);
      };
    };
    t3 = [removeBar, spyRef, state];
    $[1] = spyRef;
    $[2] = state;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== children || $[6] !== donationFormUrl || $[7] !== fundraisingEventId || $[8] !== mobileBar || $[9] !== slug || $[10] !== title) {
    t4 = <StickyBarContent donationFormUrl={donationFormUrl} stickyRef={stickyRef} mobileBar={mobileBar} fundraisingEventId={fundraisingEventId} slug={slug} title={title}>{children}</StickyBarContent>;
    $[5] = children;
    $[6] = donationFormUrl;
    $[7] = fundraisingEventId;
    $[8] = mobileBar;
    $[9] = slug;
    $[10] = title;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
export default StickyBar;